import { useState, useEffect, useMemo } from 'react'
import { useChannelBullets } from '@/hooks/useChannelBullets'
import { BULLET_KEYS, type BulletKey, bulletKeyToApiKey } from '@/components/products/v2/tabs/content/channels/channel-constants'

function buildBulletsState(): Record<BulletKey, boolean> {
  return Object.fromEntries(BULLET_KEYS.map((k) => [k, false])) as Record<BulletKey, boolean>
}

export interface UseBulletsStateArgs {
  productId?: string
  channelId: string
  includeBullets: boolean
}

export interface UseBulletsStateReturn {
  bulletFields: Record<BulletKey, string>
  bulletsDirty: Record<BulletKey, boolean>
  bulletsAvailable: boolean
  anyBulletDirty: boolean
  updateBullet: (key: BulletKey, value: string) => void
  resetBullets: () => void
  mappedBulletKeys: BulletKey[]
  keywordsMapped: boolean
  keywordsValue: string
  keywordsDirty: boolean
  updateKeywords: (value: string) => void
  anyDirty: boolean
}

export function useBulletsState({ productId, channelId, includeBullets }: UseBulletsStateArgs): UseBulletsStateReturn {
  const bulletsQuery = useChannelBullets(
    includeBullets ? productId : undefined,
    includeBullets ? channelId : undefined,
  )

  const initialBullets: Record<BulletKey, string> = useMemo(() => {
    const data = bulletsQuery.data?.bullets
    return Object.fromEntries(
      BULLET_KEYS.map((k) => [k, data?.[bulletKeyToApiKey(k)] ?? '']),
    ) as Record<BulletKey, string>
  }, [bulletsQuery.data])

  const mappedRaw = bulletsQuery.data?.mapped_fields ?? []
  const mappedBulletKeys: BulletKey[] = useMemo(
    () => BULLET_KEYS.filter((k) => mappedRaw.includes(bulletKeyToApiKey(k))),
    [mappedRaw],
  )
  const keywordsMapped = mappedRaw.includes('keywords')

  const initialKeywords = bulletsQuery.data?.keywords ?? ''

  const [bulletFields, setBulletFields] = useState<Record<BulletKey, string>>(initialBullets)
  const [bulletsDirty, setBulletsDirty] = useState<Record<BulletKey, boolean>>(() => buildBulletsState())
  const [keywordsValue, setKeywordsValue] = useState(initialKeywords)
  const [keywordsDirty, setKeywordsDirty] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional resync of bullet fields when channel/initial data changes
    setBulletFields(initialBullets)
    setBulletsDirty(buildBulletsState())
    setKeywordsValue(initialKeywords)
    setKeywordsDirty(false)
  }, [initialBullets, initialKeywords, channelId])

  const bulletsAvailable = useMemo(() => {
    if (!includeBullets) return false
    if (channelId === '1-1-0') return false // master has no bullets mapping
    return !!bulletsQuery.data
  }, [includeBullets, channelId, bulletsQuery.data])

  const anyBulletDirty = useMemo(() => BULLET_KEYS.some((k) => bulletsDirty[k]), [bulletsDirty])

  const updateBullet = (key: BulletKey, value: string) => {
    setBulletFields((prev) => ({ ...prev, [key]: value }))
    setBulletsDirty((prev) => ({ ...prev, [key]: true }))
  }

  const updateKeywords = (value: string) => {
    setKeywordsValue(value)
    setKeywordsDirty(true)
  }

  const resetBullets = () => {
    setBulletFields(initialBullets)
    setBulletsDirty(buildBulletsState())
    setKeywordsValue(initialKeywords)
    setKeywordsDirty(false)
  }

  const anyDirty = anyBulletDirty || keywordsDirty

  return {
    bulletFields,
    bulletsDirty,
    bulletsAvailable,
    anyBulletDirty,
    updateBullet,
    resetBullets,
    mappedBulletKeys,
    keywordsMapped,
    keywordsValue,
    keywordsDirty,
    updateKeywords,
    anyDirty,
  }
}
